import { z } from 'zod';
import { teamTimeZone } from './reminders.js';
import { INVITABLE_ROLES, TEAM_ROLES } from './roles.js';

/**
 * Every input shape lives here. The API validates with these; the web app
 * types its forms with them. Password *strength* is checked separately
 * (see password.ts) because its dictionaries are large.
 */

const email = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Enter your email address.')
  .max(254, 'That email address is too long.')
  .email('Enter a valid email address, like coach@example.com.');

const password = z.string().min(1, 'Enter a password.').max(128, 'Use no more than 128 characters.');

const name = z
  .string()
  .trim()
  .min(1, 'Enter your name.')
  .max(80, 'Keep your name under 80 characters.');

export const totpCode = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.');

/** Either a 6-digit app code or a recovery code (xxxxx-xxxxx). */
export const mfaCode = z
  .string()
  .trim()
  .min(6, 'Enter the 6-digit code from your authenticator app, or a recovery code.')
  .max(20, 'That code is too long.');

const token = z.string().trim().min(20, 'This link is incomplete. Copy the whole link from the email.').max(200);

// ---------- Auth ----------

export const signupInput = z.object({ name, email, password });
export type SignupInput = z.infer<typeof signupInput>;

export const loginInput = z.object({ email, password });
export type LoginInput = z.infer<typeof loginInput>;

export const loginMfaInput = z.object({ code: mfaCode });
export type LoginMfaInput = z.infer<typeof loginMfaInput>;

export const verifyEmailInput = z.object({ token });
export type VerifyEmailInput = z.infer<typeof verifyEmailInput>;

export const forgotPasswordInput = z.object({ email });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInput>;

export const resetPasswordInput = z.object({ token, password });
export type ResetPasswordInput = z.infer<typeof resetPasswordInput>;

export const changePasswordInput = z.object({ currentPassword: password, newPassword: password });
export type ChangePasswordInput = z.infer<typeof changePasswordInput>;

export const updateProfileInput = z.object({ name });
export type UpdateProfileInput = z.infer<typeof updateProfileInput>;

export const themeInput = z.object({ theme: z.enum(['system', 'light', 'dark']) });
export type ThemeInput = z.infer<typeof themeInput>;

export const mfaEnableInput = z.object({ code: totpCode });
export type MfaEnableInput = z.infer<typeof mfaEnableInput>;

export const mfaDisableInput = z.object({ password, code: mfaCode });
export type MfaDisableInput = z.infer<typeof mfaDisableInput>;

// ---------- Teams ----------

const optionalText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `Keep the ${label} under ${max} characters.`)
    .optional()
    .transform((v) => (v ? v : null));

const teamName = z.string().trim().min(1, 'Give the team a name.').max(80, 'Keep the team name under 80 characters.');

export const createTeamInput = z.object({
  name: teamName,
  ageGroup: optionalText(40, 'age group'),
  season: optionalText(40, 'season'),
});
export type CreateTeamInput = z.input<typeof createTeamInput>;

/** Fields left out stay as they are; an empty string clears a field. */
const editableText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `Keep the ${label} under ${max} characters.`)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v || null));

export const updateTeamInput = z.object({
  name: teamName.optional(),
  ageGroup: editableText(40, 'age group'),
  season: editableText(40, 'season'),
  timeZone: teamTimeZone.optional(),
});
export type UpdateTeamInput = z.input<typeof updateTeamInput>;

export const teamRole = z.enum(TEAM_ROLES);
export const invitableRole = z.enum(INVITABLE_ROLES, {
  errorMap: () => ({ message: 'Choose a role for this person.' }),
});

export const createInviteInput = z.object({ email, role: invitableRole });
export type CreateInviteInput = z.infer<typeof createInviteInput>;

export const updateMemberInput = z.object({ role: invitableRole });
export type UpdateMemberInput = z.infer<typeof updateMemberInput>;

export const acceptInviteInput = z.object({ token });

export const uuid = z.string().uuid('That link or ID isn’t valid.');
